import axios from 'axios';
import { getAuthHeaders } from './auth';
import {
  readProfilePictureBrowserBlob,
  writeProfilePictureBrowserBlob,
} from './profilePictureBrowserCache';

const cache = new Map();

export function patientProfilePictureRevision(persona) {
  if (!persona) return 0;
  const rev = persona.profile_picture_revision;
  const n = Number(rev);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export function patientHasProfilePicture(persona) {
  if (!persona) return false;
  const flag = persona.has_profile_picture;
  return flag === true || flag === 't' || flag === 'true' || flag === 1;
}

function cacheKey(personaId, revision) {
  return `${personaId}:${revision ?? 0}`;
}

function fullCacheKey(personaId, revision) {
  return `full:${personaId}:${revision ?? 0}`;
}

function blobToObjectUrl(key, pending, blob) {
  if (cache.get(key) !== pending) return null;
  const url = URL.createObjectURL(blob);
  cache.set(key, url);
  return url;
}

async function fetchProfilePictureBlob(apiPath, personaId, revision) {
  const rev = Number(revision) || 0;
  const res = await axios.get(apiPath, {
    params: { v: rev },
    responseType: 'blob',
    headers: getAuthHeaders(),
  });
  const contentType = res.headers?.['content-type'] || res.data?.type || 'image/webp';
  return { blob: res.data, contentType };
}

function loadCachedProfilePicture(storageKey, apiPath, personaId, revision) {
  const existing = cache.get(storageKey);
  if (existing && typeof existing === 'string') return Promise.resolve(existing);
  if (existing) return existing;

  const pending = (async () => {
    const cachedBlob = await readProfilePictureBrowserBlob(storageKey);
    if (cachedBlob) {
      const fromBrowser = blobToObjectUrl(storageKey, pending, cachedBlob);
      if (fromBrowser) return fromBrowser;
    }

    const { blob, contentType } = await fetchProfilePictureBlob(apiPath, personaId, revision);
    writeProfilePictureBrowserBlob(storageKey, blob, contentType);
    return blobToObjectUrl(storageKey, pending, blob);
  })().catch((err) => {
    if (cache.get(storageKey) === pending) cache.delete(storageKey);
    throw err;
  });

  cache.set(storageKey, pending);
  return pending;
}

/** @visibleForTesting */
export function clearPatientProfilePictureMemoryCache() {
  for (const value of cache.values()) {
    if (typeof value === 'string' && typeof URL.revokeObjectURL === 'function') {
      URL.revokeObjectURL(value);
    }
  }
  cache.clear();
}

export function loadPatientProfilePicture(personaId, revision = 0) {
  const rev = Number(revision) || 0;
  return loadCachedProfilePicture(
    cacheKey(personaId, rev),
    `/api/personas/${personaId}/profile-picture`,
    personaId,
    rev,
  );
}

export function loadPatientProfilePictureFull(personaId, revision = 0) {
  const rev = Number(revision) || 0;
  return loadCachedProfilePicture(
    fullCacheKey(personaId, rev),
    `/api/personas/${personaId}/profile-picture/full`,
    personaId,
    rev,
  );
}
